import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button, App as AntdApp, Spin, Card, Dropdown, Tag, Tooltip,
  Empty, Modal, Input, Switch, Select, InputNumber, Divider,
  Form, Space, Alert,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Save,
  Image, Heading1, Heading2, Type, AlignJustify, Square,
  TableProperties, ListChecks, Edit3, Eye, Code,
  RotateCcw, Copy, LayoutTemplate, Sparkles,
} from 'lucide-react';
import { templateService } from '@/services/templateService';
import { userService } from '@/services/userService';
import { orgService } from '@/services/rolesService';
import { useAuthStore } from '@/store/authStore';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';
import { systemSettingsService } from '@/services/systemSettingsService';
import {
  parseFormSchema, FormSchema, FormBlock, FieldBlock,
  TableBlock, ChecklistBlock, CheckboxRowBlock, FieldRowBlock,
  HeaderBlock, TitleBlock, TextBlock, SectionTitleBlock, SignaturesBlock,
  TableColumn, TableTotal,
  Header2ColBlock, TableGroupedBlock, PartySectionBlock, BankInfoBlock,
  InfoGridBlock, SpacerBlock,
} from '@/utils/formSchema';
import { TEMPLATE_GALLERY } from '@/utils/templateGallery';
import { SortableList } from '@/components/builder/SortableList';
import { ExpressionBuilder } from '@/components/builder/ExpressionBuilder';
import { BlockRenderer } from './PaperFillPage';

/**
 * Visual Builder cho form schema:
 * - Cột trái: list block + nút "+ thêm block" + reorder
 * - Cột giữa: live preview giấy A4
 * - Cột phải: property editor cho block đang chọn
 *
 * Chỉ cần kéo-thả/click, không cần biết JSON.
 * Tab JSON cho power user vẫn xem/sửa được raw.
 */

export function TemplateBuilderPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { message, modal } = AntdApp.useApp();
  const { user } = useAuthStore();
  const { settings, hasLogo, logoVersion } = useSystemSettings();

  const [schema, setSchema] = useState<FormSchema>({ version: 1, blocks: [] });
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'visual' | 'json' | 'test'>('visual');
  const [galleryOpen, setGalleryOpen] = useState(false);
  // Test mode state — admin fill data thử để xem render
  const [testValues, setTestValues] = useState<Record<string, string>>({});
  const [testTables, setTestTables] = useState<Record<string, any[]>>({});
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const { data: template, isLoading, refetch } = useQuery({
    queryKey: ['template', id],
    queryFn: () => templateService.getById(id!),
    enabled: !!id,
  });

  // Init schema từ template - dùng schema có sẵn hoặc default
  useEffect(() => {
    if (!template) return;
    const parsed = parseFormSchema(template.formSchemaJson);
    if (parsed) {
      setSchema(parsed);
      setJsonText(JSON.stringify(parsed, null, 2));
    } else {
      // Empty schema mới
      const empty: FormSchema = { version: 1, blocks: [] };
      setSchema(empty);
      setJsonText(JSON.stringify(empty, null, 2));
    }
    setDirty(false);
  }, [template]);

  const updateSchema = (next: FormSchema) => {
    setSchema(next);
    setJsonText(JSON.stringify(next, null, 2));
    setDirty(true);
  };

  // Block operations
  const addBlock = (type: FormBlock['type']) => {
    const newBlock = createDefaultBlock(type);
    const next = { ...schema, blocks: [...schema.blocks, newBlock] };
    updateSchema(next);
    setSelectedIdx(next.blocks.length - 1);
  };

  const removeBlock = (idx: number) => {
    modal.confirm({
      title: 'Xóa block này?',
      content: 'Block và các thuộc tính của nó sẽ bị mất.',
      okText: 'Xóa', cancelText: 'Hủy', okButtonProps: { danger: true },
      onOk: () => {
        const next = { ...schema, blocks: schema.blocks.filter((_, i) => i !== idx) };
        updateSchema(next);
        if (selectedIdx === idx) setSelectedIdx(null);
        else if (selectedIdx !== null && selectedIdx > idx) setSelectedIdx(selectedIdx - 1);
      },
    });
  };

  const moveBlock = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= schema.blocks.length) return;
    const blocks = [...schema.blocks];
    [blocks[idx], blocks[newIdx]] = [blocks[newIdx], blocks[idx]];
    updateSchema({ ...schema, blocks });
    if (selectedIdx === idx) setSelectedIdx(newIdx);
    else if (selectedIdx === newIdx) setSelectedIdx(idx);
  };

  const duplicateBlock = (idx: number) => {
    const cloned = JSON.parse(JSON.stringify(schema.blocks[idx])) as FormBlock;
    // Đổi tên cho tránh trùng
    if ('name' in cloned && cloned.name) {
      (cloned as any).name = cloned.name + '_copy';
    }
    const blocks = [...schema.blocks];
    blocks.splice(idx + 1, 0, cloned);
    updateSchema({ ...schema, blocks });
    setSelectedIdx(idx + 1);
  };

  const updateBlock = (idx: number, updated: FormBlock) => {
    const blocks = [...schema.blocks];
    blocks[idx] = updated;
    updateSchema({ ...schema, blocks });
  };

  const updateMeta = (patch: Partial<FormSchema>) => {
    updateSchema({ ...schema, ...patch });
  };

  // JSON mode handlers
  const applyJsonText = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed.blocks)) {
        setJsonError('Schema phải có mảng "blocks"');
        return;
      }
      setJsonError(null);
      setSchema(parsed);
      setDirty(true);
      message.success('Đã apply JSON');
    } catch (e: any) {
      setJsonError('JSON không hợp lệ: ' + e.message);
    }
  };

  // Save
  const handleSave = async () => {
    if (!template) return;
    setSaving(true);
    try {
      await templateService.update(template.id, {
        name: template.name,
        description: template.description,
        categoryId: template.categoryId,
        departmentId: template.departmentId,
        isActive: template.isActive,
        formSchemaJson: JSON.stringify(schema),
      });
      message.success('Đã lưu schema');
      setDirty(false);
      refetch();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  };

  // Reset to default DNTT (chỉ cho mẫu code DNTT)
  const handleResetDefault = () => {
    if (!template) return;
    modal.confirm({
      title: 'Đặt lại schema mặc định?',
      content: 'Toàn bộ chỉnh sửa hiện tại sẽ bị mất.',
      okText: 'Đặt lại', cancelText: 'Hủy', okButtonProps: { danger: true },
      onOk: async () => {
        // Lưu empty -> backend sẽ fallback về default
        try {
          await templateService.update(template.id, {
            name: template.name,
            description: template.description,
            categoryId: template.categoryId,
            departmentId: template.departmentId,
            isActive: template.isActive,
            formSchemaJson: '',
          });
          await refetch();
          message.success('Đã đặt lại');
        } catch (e: any) {
          message.error('Đặt lại thất bại');
        }
      },
    });
  };

  if (isLoading || !template) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;
  }

  const useTextLogo = settings.logo_display_mode === 'text';
  const logoUrl = !useTextLogo && hasLogo
    ? `${systemSettingsService.logoUrl()}?v=${logoVersion}`
    : null;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      {/* Header bar */}
      <div style={{
        background: '#fff', padding: '10px 16px',
        borderBottom: '1px solid #e8e8e8',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Button icon={<ArrowLeft size={14} />} onClick={() => {
          if (dirty) {
            modal.confirm({
              title: 'Có thay đổi chưa lưu',
              content: 'Bạn có chắc muốn rời?',
              okText: 'Rời đi', cancelText: 'Ở lại',
              onOk: () => navigate('/admin/templates'),
            });
          } else {
            navigate('/admin/templates');
          }
        }}>
          Quay lại
        </Button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            Visual Builder: {template.name}
            {dirty && <Tag color="orange" style={{ marginLeft: 8 }}>Chưa lưu</Tag>}
          </div>
          <div style={{ fontSize: 11, color: '#7d8a98' }}>
            <code>{template.code}</code> · {schema.blocks.length} block
          </div>
        </div>

        <Button.Group>
          <Button
            type={viewMode === 'visual' ? 'primary' : 'default'}
            icon={<Edit3 size={13} />}
            onClick={() => setViewMode('visual')}
          >
            Visual
          </Button>
          <Button
            type={viewMode === 'test' ? 'primary' : 'default'}
            icon={<Eye size={13} />}
            onClick={() => {
              setViewMode('test');
              // Reset test data khi vào mode test
              setTestValues({});
              setTestTables({});
            }}
          >
            Live Test
          </Button>
          <Button
            type={viewMode === 'json' ? 'primary' : 'default'}
            icon={<Code size={13} />}
            onClick={() => setViewMode('json')}
          >
            JSON
          </Button>
        </Button.Group>

        <Tooltip title="Áp dụng schema từ thư viện mẫu sẵn">
          <Button icon={<LayoutTemplate size={13} />} onClick={() => setGalleryOpen(true)}>
            Thư viện
          </Button>
        </Tooltip>

        {template.code === 'DNTT' && (
          <Tooltip title="Đặt lại schema mặc định cho DNTT">
            <Button icon={<RotateCcw size={13} />} onClick={handleResetDefault}>
              Reset
            </Button>
          </Tooltip>
        )}

        <Button
          type="primary"
          icon={<Save size={14} />}
          loading={saving}
          disabled={!dirty}
          onClick={handleSave}
        >
          Lưu
        </Button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {viewMode === 'visual' ? (
          <>
            {/* Sidebar trái: block list */}
            <div style={{
              width: 280, background: '#fff',
              borderRight: '1px solid #e8e8e8',
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                <AddBlockMenu onAdd={addBlock} />
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
                {schema.blocks.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Chưa có block nào"
                    style={{ marginTop: 60 }}
                  />
                ) : (
                  <SortableList
                    items={schema.blocks.map((b, i) => ({ id: `block-${i}-${b.type}`, data: b }))}
                    onReorder={(newItems) => {
                      // Track selected -> giữ selected sau reorder
                      const selectedBlock = selectedIdx !== null ? schema.blocks[selectedIdx] : null;
                      const newBlocks = newItems.map(i => i.data);
                      updateSchema({ ...schema, blocks: newBlocks });
                      if (selectedBlock) {
                        const newIdx = newBlocks.indexOf(selectedBlock);
                        setSelectedIdx(newIdx >= 0 ? newIdx : null);
                      }
                    }}
                    renderItem={(item, dragHandle, i) => (
                      <BlockListItem
                        block={item.data}
                        index={i}
                        selected={selectedIdx === i}
                        dragHandle={dragHandle}
                        onSelect={() => setSelectedIdx(i)}
                        onDuplicate={() => duplicateBlock(i)}
                        onRemove={() => removeBlock(i)}
                      />
                    )}
                  />
                )}
              </div>
            </div>

            {/* Giữa: live preview */}
            <div style={{ flex: 1, overflow: 'auto', padding: 16, background: '#e8eaed' }}>
              <div style={{
                maxWidth: 794,
                margin: '0 auto',
                background: '#fff',
                padding: '32px 48px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                fontFamily: '"Times New Roman", Times, serif',
                color: '#000',
                fontSize: 14,
                lineHeight: 1.6,
              }}>
                {schema.blocks.length === 0 ? (
                  <div style={{ padding: '80px 0', textAlign: 'center', color: '#bfbfbf' }}>
                    Thêm block từ panel bên trái
                  </div>
                ) : (
                  schema.blocks.map((block, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedIdx(idx)}
                      style={{
                        cursor: 'pointer',
                        outline: selectedIdx === idx ? '2px solid #1890ff' : '2px solid transparent',
                        outlineOffset: 2, borderRadius: 2,
                        position: 'relative',
                      }}
                    >
                      <BlockRenderer
                        block={block}
                        values={previewValues}
                        tables={previewTables}
                        tableTotals={previewTableTotals}
                        settings={settings}
                        logoUrl={logoUrl}
                        user={user}
                        formCode={schema.formCode}
                        onSetValue={() => { }}
                        onToggleExclusive={() => { }}
                        onUpdateTableCell={() => { }}
                        onAddTableRow={() => { }}
                        onRemoveTableRow={() => { }}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Sidebar phải: property editor */}
            <div style={{
              width: 340, background: '#fff',
              borderLeft: '1px solid #e8e8e8',
              overflow: 'auto',
            }}>
              {selectedIdx === null ? (
                <SchemaMetaEditor schema={schema} onChange={updateMeta} />
              ) : (
                <BlockPropertyEditor
                  block={schema.blocks[selectedIdx]}
                  allBlocks={schema.blocks}
                  onChange={(b) => updateBlock(selectedIdx, b)}
                />
              )}
            </div>
          </>
        ) : viewMode === 'test' ? (
          // Live Test mode - admin fill data thử
          <LiveTestMode
            schema={schema}
            settings={settings}
            logoUrl={logoUrl}
            user={user}
            values={testValues}
            tables={testTables}
            onSetValues={setTestValues}
            onSetTables={setTestTables}
          />
        ) : (
          // JSON mode
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 16 }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Sửa JSON trực tiếp"
              description="Dành cho người dùng có kinh nghiệm. Bấm 'Áp dụng' để cập nhật, sau đó 'Lưu' để ghi vào DB."
            />
            {jsonError && (
              <Alert type="error" message={jsonError} style={{ marginBottom: 12 }} />
            )}
            <Input.TextArea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}
              autoSize={{ minRows: 20 }}
            />
            <Space style={{ marginTop: 12 }}>
              <Button onClick={applyJsonText} icon={<Eye size={13} />}>
                Áp dụng (xem trước)
              </Button>
              <Button onClick={() => setJsonText(JSON.stringify(schema, null, 2))}>
                Format
              </Button>
            </Space>
          </div>
        )}
      </div>

      {/* Gallery modal */}
      {galleryOpen && (
        <GalleryModal
          onClose={() => setGalleryOpen(false)}
          onApply={(gItem) => {
            modal.confirm({
              title: `Áp dụng schema "${gItem.name}"?`,
              content: 'Toàn bộ schema hiện tại sẽ bị thay thế. Hành động này không reverse được sau khi Lưu.',
              okText: 'Áp dụng', cancelText: 'Hủy', okButtonProps: { danger: true },
              onOk: () => {
                updateSchema(gItem.schema);
                setSelectedIdx(null);
                setGalleryOpen(false);
                message.success(`Đã áp dụng schema "${gItem.name}". Bấm Lưu để ghi vào DB.`);
              },
            });
          }}
        />
      )}
    </div>
  );
}

// Preview values (chỉ để render đẹp trong builder)
const previewValues: Record<string, string> = {
  is_advance: '0', is_payment: '1',
  ngay: '2026-05-05',
  ho_ten: 'Phạm Ngọc Ánh', bo_phan: 'CNTT',
  phu_trach_ncc: 'Công ty ABC',
  ly_do: 'Mua thiết bị máy in văn phòng phục vụ công việc',
  tam_ung: '0', chi_khong_het: '0',
  con_phai_tt: '5500000',
  con_phai_tt_words: 'Năm triệu năm trăm nghìn đồng',
  ct_1: '1', ct_1_text: 'PO 2026/05/01',
  ct_2: '0',
};
const previewTables: Record<string, any[]> = {
  items: [
    { noi_dung: 'Máy in HP LaserJet Pro', dvt: 'Cái', so_luong: '2', don_gia: '2750000', thue: '0', thanh_tien: '5500000' },
  ],
};
const previewTableTotals: Record<string, number> = { tong_cong: 5500000 };

// =============================================================
// Add block menu
// =============================================================
function AddBlockMenu({ onAdd }: { onAdd: (type: FormBlock['type']) => void }) {
  const items = [
    { key: 'header', icon: <Image size={14} />, label: 'Header (logo + tên cty)' },
    { key: 'header_2col', icon: <Image size={14} />, label: 'Header 2 cột (Sở YT / CHXHCN VN)' },
    { key: 'title', icon: <Heading1 size={14} />, label: 'Tiêu đề lớn' },
    { key: 'section_title', icon: <Heading2 size={14} />, label: 'Tiêu đề mục (I, II...)' },
    { key: 'text', icon: <Type size={14} />, label: 'Đoạn text' },
    { key: 'spacer', icon: <AlignJustify size={14} />, label: 'Khoảng trống / Đường kẻ' },
    { type: 'divider' as const },
    { key: 'field', icon: <Edit3 size={14} />, label: 'Trường nhập (1 dòng)' },
    { key: 'field_row', icon: <AlignJustify size={14} />, label: 'Nhiều trường cùng dòng' },
    { key: 'info_grid', icon: <TableProperties size={14} />, label: 'Grid thông tin (2-4 ô có viền)' },
    { key: 'checkbox_row', icon: <Square size={14} />, label: 'Checkbox cùng dòng' },
    { key: 'checklist', icon: <ListChecks size={14} />, label: 'Danh sách checkbox' },
    { type: 'divider' as const },
    { key: 'table', icon: <TableProperties size={14} />, label: 'Bảng dữ liệu' },
    { key: 'table_grouped', icon: <TableProperties size={14} />, label: 'Bảng nhóm cột (multi header)' },
    { type: 'divider' as const },
    { key: 'party_section', icon: <AlignJustify size={14} />, label: 'Bên A/B (đại diện)' },
    { key: 'bank_info', icon: <Edit3 size={14} />, label: 'Thông tin ngân hàng' },
    { key: 'signatures', icon: <Edit3 size={14} />, label: 'Vùng chữ ký' },
  ];

  return (
    <Dropdown
      menu={{
        items: items.map((it: any) => {
          if (it.type === 'divider') return { type: 'divider' as const };
          return {
            key: it.key, icon: it.icon, label: it.label,
            onClick: () => onAdd(it.key as any),
          };
        }),
      }}
      trigger={['click']}
    >
      <Button type="primary" block icon={<Plus size={14} />}>
        Thêm block
      </Button>
    </Dropdown>
  );
}

// =============================================================
// Block list item (sidebar)
// =============================================================
function BlockListItem({
  block, index, selected, dragHandle,
  onSelect, onDuplicate, onRemove,
}: {
  block: FormBlock; index: number; selected: boolean;
  dragHandle: React.ReactNode;
  onSelect: () => void;
  onDuplicate: () => void; onRemove: () => void;
}) {
  const meta = blockTypeMeta(block.type);
  return (
    <div
      onClick={onSelect}
      style={{
        padding: '6px 8px', marginBottom: 4,
        borderRadius: 4, cursor: 'pointer',
        background: selected ? '#e6f4ff' : '#fafafa',
        border: '1px solid ' + (selected ? '#1890ff' : 'transparent'),
        display: 'flex', alignItems: 'center', gap: 4,
      }}
    >
      {dragHandle}
      <span style={{ flexShrink: 0, color: '#1976D2' }}>{meta.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}>
          {meta.label}
        </div>
        <div style={{ fontSize: 11, color: '#8a95a5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {blockSummary(block)}
        </div>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Nhân bản">
          <button onClick={onDuplicate} style={iconBtnStyle}>
            <Copy size={11} />
          </button>
        </Tooltip>
        <Tooltip title="Xóa">
          <button onClick={onRemove} style={{ ...iconBtnStyle, color: '#ff4d4f' }}>
            <Trash2 size={11} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  width: 20, height: 20,
  border: 0, background: 'transparent',
  cursor: 'pointer', display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
  borderRadius: 3, color: '#5b6875',
};

function blockTypeMeta(type: FormBlock['type']): { label: string; icon: any } {
  switch (type) {
    case 'header': return { label: 'Header', icon: <Image size={14} /> };
    case 'header_2col': return { label: 'Header 2 cột', icon: <Image size={14} /> };
    case 'title': return { label: 'Tiêu đề lớn', icon: <Heading1 size={14} /> };
    case 'section_title': return { label: 'Tiêu đề mục', icon: <Heading2 size={14} /> };
    case 'text': return { label: 'Text', icon: <Type size={14} /> };
    case 'field': return { label: 'Trường nhập', icon: <Edit3 size={14} /> };
    case 'field_row': return { label: 'Hàng nhiều trường', icon: <AlignJustify size={14} /> };
    case 'checkbox_row': return { label: 'Checkbox row', icon: <Square size={14} /> };
    case 'table': return { label: 'Bảng', icon: <TableProperties size={14} /> };
    case 'table_grouped': return { label: 'Bảng nhóm cột', icon: <TableProperties size={14} /> };
    case 'checklist': return { label: 'Checklist', icon: <ListChecks size={14} /> };
    case 'signatures': return { label: 'Chữ ký', icon: <Edit3 size={14} /> };
    case 'party_section': return { label: 'Bên A/B (đại diện)', icon: <AlignJustify size={14} /> };
    case 'bank_info': return { label: 'Thông tin ngân hàng', icon: <Edit3 size={14} /> };
    case 'info_grid': return { label: 'Grid thông tin (2-4 ô)', icon: <TableProperties size={14} /> };
    case 'spacer': return { label: 'Khoảng trống / Đường kẻ', icon: <AlignJustify size={14} /> };
  }
}

function blockSummary(block: FormBlock): string {
  switch (block.type) {
    case 'header': return 'Logo + tên công ty';
    case 'header_2col': return `${block.left.line1} | ${block.right.line1}`;
    case 'title': return block.text;
    case 'section_title': return block.text;
    case 'text': return block.text.length > 40 ? block.text.slice(0, 40) + '…' : block.text;
    case 'field': return `${block.label} [${block.name}]`;
    case 'field_row': return block.fields.map(f => f.label).join(' + ');
    case 'checkbox_row': return block.items.map(i => i.label).join(' / ');
    case 'table': return `${block.columns.length} cột${block.addable ? ', có thể thêm dòng' : ''}`;
    case 'table_grouped': return `${block.groups.length} nhóm, ${block.columns.length} cột`;
    case 'checklist': return `${block.items.length} mục`;
    case 'signatures': return `${block.items.length} chữ ký${block.layout === 'grid' ? ' (grid)' : ''}`;
    case 'party_section': return `${block.title} (${block.defaultReps ?? 2} đại diện)`;
    case 'bank_info': return `Bank: ${block.beneficiaryName ?? 'beneficiary'} / ${block.accountNumberName ?? 'account_number'}`;
    case 'info_grid': return `Grid ${block.columns} ô: ${block.cells.map(c => c.label).join(' | ')}`;
    case 'spacer': return block.kind === 'line' ? 'Đường kẻ ngang' : `Khoảng trống ${block.height ?? 16}px`;
  }
}

// =============================================================
// Schema-level meta editor (page settings)
// =============================================================
function SchemaMetaEditor({ schema, onChange }: {
  schema: FormSchema;
  onChange: (patch: Partial<FormSchema>) => void;
}) {
  return (
    <div style={{ padding: 16 }}>
      <h3 style={{ fontSize: 13, fontWeight: 600, marginTop: 0 }}>Cài đặt chung</h3>
      <p style={{ fontSize: 11, color: '#8a95a5', marginBottom: 16 }}>
        Click vào block ở giữa hoặc trái để chỉnh thuộc tính từng block.
      </p>
      <Form layout="vertical" size="small">
        <Form.Item label="Mã form (góc phải đầu)" extra="VD: TUTT/2025/04">
          <Input
            value={schema.formCode ?? ''}
            onChange={(e) => onChange({ formCode: e.target.value })}
            placeholder="TUTT/2025/04"
          />
        </Form.Item>
        <Form.Item label="Khổ giấy">
          <Select
            value={schema.paperSize ?? 'A4'}
            onChange={(v) => onChange({ paperSize: v as any })}
            options={[
              { value: 'A4', label: 'A4 (210×297mm)' },
              { value: 'A5', label: 'A5 (148×210mm)' },
            ]}
          />
        </Form.Item>
      </Form>
    </div>
  );
}

// =============================================================
// Property editor cho từng loại block
// =============================================================
function BlockPropertyEditor({ block, allBlocks, onChange }: {
  block: FormBlock;
  allBlocks: FormBlock[];
  onChange: (b: FormBlock) => void;
}) {
  // Tính danh sách biến available cho computed expression
  const availableVars = useMemo(() => collectAvailableVars(allBlocks), [allBlocks]);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <Tag color="blue">{blockTypeMeta(block.type).label}</Tag>
      </div>
      {(() => {
        switch (block.type) {
          case 'header': return <HeaderEditor block={block} onChange={onChange} />;
          case 'title': return <TitleEditor block={block} onChange={onChange} />;
          case 'section_title': return <SectionTitleEditor block={block} onChange={onChange} />;
          case 'text': return <TextEditor block={block} onChange={onChange} />;
          case 'field': return <FieldEditor block={block} onChange={onChange} availableVars={availableVars} />;
          case 'field_row': return <FieldRowEditor block={block} onChange={onChange} />;
          case 'checkbox_row': return <CheckboxRowEditor block={block} onChange={onChange} />;
          case 'table': return <TableEditor block={block} onChange={onChange} />;
          case 'checklist': return <ChecklistEditor block={block} onChange={onChange} />;
          case 'signatures': return <SignaturesEditor block={block} onChange={onChange} />;
          case 'header_2col': return <Header2ColEditor block={block} onChange={onChange} />;
          case 'table_grouped': return <TableGroupedEditor block={block} onChange={onChange} />;
          case 'party_section': return <PartySectionEditor block={block} onChange={onChange} />;
          case 'bank_info': return <BankInfoEditor block={block} onChange={onChange} />;
          case 'info_grid': return <InfoGridEditor block={block} onChange={onChange} />;
          case 'spacer': return <SpacerEditor block={block} onChange={onChange} />;
          default: return null;
        }
      })()}
    </div>
  );
}

/** Quét toàn schema để liệt kê tên biến có thể dùng trong computed. */
function collectAvailableVars(blocks: FormBlock[]): string[] {
  const vars: string[] = [];
  for (const b of blocks) {
    if (b.type === 'field') vars.push(b.name);
    if (b.type === 'field_row') b.fields.forEach(f => vars.push(f.name));
    if (b.type === 'table' && b.totals) b.totals.forEach(t => vars.push(t.name));
  }
  return Array.from(new Set(vars));
}

// ----------------- HEADER -----------------
function HeaderEditor({ block, onChange }: { block: HeaderBlock; onChange: (b: HeaderBlock) => void }) {
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Hiển thị logo" valuePropName="checked">
        <Switch checked={block.showLogo !== false}
          onChange={(v) => onChange({ ...block, showLogo: v })} />
      </Form.Item>
      <Form.Item label="Hiển thị tên công ty" valuePropName="checked">
        <Switch checked={block.showCompany !== false}
          onChange={(v) => onChange({ ...block, showCompany: v })} />
      </Form.Item>
      <Alert type="info" message="Logo & tên công ty lấy từ Cài đặt hệ thống." style={{ fontSize: 12 }} />
    </Form>
  );
}

// ----------------- TITLE -----------------
function TitleEditor({ block, onChange }: { block: TitleBlock; onChange: (b: TitleBlock) => void }) {
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Nội dung tiêu đề">
        <Input value={block.text} onChange={(e) => onChange({ ...block, text: e.target.value })} />
      </Form.Item>
    </Form>
  );
}

// ----------------- SECTION TITLE -----------------
function SectionTitleEditor({ block, onChange }: { block: SectionTitleBlock; onChange: (b: SectionTitleBlock) => void }) {
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Nội dung tiêu đề mục" extra="VD: I - Số tiền đề nghị">
        <Input value={block.text} onChange={(e) => onChange({ ...block, text: e.target.value })} />
      </Form.Item>
    </Form>
  );
}

// ----------------- TEXT -----------------
function TextEditor({ block, onChange }: { block: TextBlock; onChange: (b: TextBlock) => void }) {
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Nội dung">
        <Input.TextArea rows={3} value={block.text} onChange={(e) => onChange({ ...block, text: e.target.value })} />
      </Form.Item>
      <Form.Item label="Căn chữ">
        <Select value={block.align ?? 'left'} onChange={(v) => onChange({ ...block, align: v })}
          options={[
            { value: 'left', label: 'Trái' },
            { value: 'center', label: 'Giữa' },
            { value: 'right', label: 'Phải' },
          ]} />
      </Form.Item>
      <Form.Item valuePropName="checked">
        <Switch checked={!!block.bold} onChange={(v) => onChange({ ...block, bold: v })} />
        <span style={{ marginLeft: 8 }}>In đậm</span>
      </Form.Item>
      <Form.Item valuePropName="checked">
        <Switch checked={!!block.italic} onChange={(v) => onChange({ ...block, italic: v })} />
        <span style={{ marginLeft: 8 }}>In nghiêng</span>
      </Form.Item>
    </Form>
  );
}

// ----------------- FIELD -----------------
function FieldEditor({ block, onChange, availableVars }: {
  block: FieldBlock;
  onChange: (b: FieldBlock) => void;
  availableVars: string[];
}) {
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Nhãn (label)" required>
        <Input value={block.label} onChange={(e) => onChange({ ...block, label: e.target.value })} />
      </Form.Item>
      <Form.Item
        label="Tên field (name)"
        required
        extra="Chỉ chữ thường, số, gạch dưới. VD: ho_ten, so_tien"
      >
        <Input value={block.name}
          onChange={(e) => onChange({ ...block, name: e.target.value.replace(/[^a-z0-9_]/g, '') })}
          placeholder="ten_field" />
      </Form.Item>
      <Form.Item label="Loại nhập">
        <Select value={block.input}
          onChange={(v) => onChange({ ...block, input: v })}
          options={[
            { value: 'text', label: 'Văn bản 1 dòng' },
            { value: 'textarea', label: 'Văn bản nhiều dòng' },
            { value: 'number', label: 'Số' },
            { value: 'date', label: 'Ngày' },
          ]} />
      </Form.Item>
      <Form.Item label="Bắt buộc" valuePropName="checked">
        <Switch checked={!!block.required} onChange={(v) => onChange({ ...block, required: v })} />
      </Form.Item>
      <Form.Item label="Tự fill" extra="Tự điền giá trị từ user/today, user vẫn sửa được">
        <Select allowClear value={block.autoFill ?? undefined}
          onChange={(v) => onChange({ ...block, autoFill: v as any })}
          options={[
            { value: 'current_user_name', label: 'Tên user đăng nhập' },
            { value: 'current_user_dept', label: 'Khoa/phòng của user' },
            { value: 'today', label: 'Ngày hôm nay' },
          ]} placeholder="(không)" />
      </Form.Item>
      <Form.Item label="Hậu tố sau ô nhập" extra="VD: 'đồng'">
        <Input value={block.suffix ?? ''} onChange={(e) => onChange({ ...block, suffix: e.target.value || undefined })} />
      </Form.Item>
      <Divider style={{ margin: '12px 0' }} />
      <Form.Item label="Thụt lề trái" valuePropName="checked">
        <Switch checked={!!block.indent} onChange={(v) => onChange({ ...block, indent: v })} />
      </Form.Item>
      <Form.Item label="In nghiêng" valuePropName="checked">
        <Switch checked={!!block.italic} onChange={(v) => onChange({ ...block, italic: v })} />
      </Form.Item>

      <Divider style={{ margin: '12px 0' }} />
      <h4 style={{ fontSize: 12, fontWeight: 600 }}>
        <Sparkles size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
        Tính toán tự động
      </h4>
      <Form.Item label="Công thức tính">
        <ExpressionBuilder
          value={block.computed}
          onChange={(v) => onChange({ ...block, computed: v, readonly: !!v || !!block.readonly })}
          availableVars={availableVars}
        />
      </Form.Item>
      <Form.Item
        label="Đổi số sang chữ tiếng Việt"
        extra="Tên field số nguồn. VD: 'con_phai_tt'"
      >
        <Select
          allowClear
          value={block.computedWords ?? undefined}
          onChange={(v) => onChange({ ...block, computedWords: v ?? undefined, readonly: !!v || !!block.readonly })}
          options={availableVars.map(v => ({ value: v, label: v }))}
          placeholder="(không có)"
          showSearch
        />
      </Form.Item>
      <Form.Item label="Chỉ đọc" valuePropName="checked">
        <Switch checked={!!block.readonly} onChange={(v) => onChange({ ...block, readonly: v })} />
      </Form.Item>
    </Form>
  );
}

// ----------------- FIELD ROW -----------------
function FieldRowEditor({ block, onChange }: { block: FieldRowBlock; onChange: (b: FieldRowBlock) => void }) {
  const updateField = (idx: number, patch: Partial<Omit<FieldBlock, 'type'>>) => {
    const fields = [...block.fields];
    fields[idx] = { ...fields[idx], ...patch };
    onChange({ ...block, fields });
  };
  const addField = () => {
    onChange({ ...block, fields: [...block.fields, { name: 'field_' + Date.now(), label: 'Field mới', input: 'text' }] });
  };
  const removeField = (idx: number) => {
    onChange({ ...block, fields: block.fields.filter((_, i) => i !== idx) });
  };

  return (
    <Form layout="vertical" size="small">
      <p style={{ fontSize: 11, color: '#8a95a5' }}>
        Tất cả các field sẽ hiển thị trên cùng 1 dòng (chia đều).
      </p>
      {block.fields.map((f, i) => (
        <Card size="small" key={i} style={{ marginBottom: 8, background: '#fafafa' }}
          title={`Field #${i + 1}`}
          extra={
            <Button danger size="small" type="text"
              icon={<Trash2 size={12} />}
              onClick={() => removeField(i)} />
          }>
          <Form.Item label="Nhãn">
            <Input size="small" value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} />
          </Form.Item>
          <Form.Item label="Tên field">
            <Input size="small" value={f.name}
              onChange={(e) => updateField(i, { name: e.target.value.replace(/[^a-z0-9_]/g, '') })} />
          </Form.Item>
          <Form.Item label="Loại">
            <Select size="small" value={f.input} onChange={(v) => updateField(i, { input: v })}
              options={[
                { value: 'text', label: 'Văn bản' },
                { value: 'number', label: 'Số' },
                { value: 'date', label: 'Ngày' },
              ]} />
          </Form.Item>
          <Form.Item label="Tự fill">
            <Select size="small" allowClear
              value={f.autoFill ?? undefined}
              onChange={(v) => updateField(i, { autoFill: v as any })}
              options={[
                { value: 'current_user_name', label: 'Tên user' },
                { value: 'current_user_dept', label: 'Khoa user' },
                { value: 'today', label: 'Hôm nay' },
              ]} />
          </Form.Item>
        </Card>
      ))}
      <Button block type="dashed" icon={<Plus size={13} />} onClick={addField}>
        Thêm field
      </Button>
    </Form>
  );
}

// ----------------- CHECKBOX ROW -----------------
function CheckboxRowEditor({ block, onChange }: { block: CheckboxRowBlock; onChange: (b: CheckboxRowBlock) => void }) {
  const updateItem = (idx: number, patch: Partial<typeof block.items[0]>) => {
    const items = [...block.items];
    items[idx] = { ...items[idx], ...patch };
    onChange({ ...block, items });
  };
  const addItem = () => {
    onChange({ ...block, items: [...block.items, { name: 'cb_' + Date.now(), label: 'Mới' }] });
  };
  const removeItem = (idx: number) => {
    onChange({ ...block, items: block.items.filter((_, i) => i !== idx) });
  };

  return (
    <Form layout="vertical" size="small">
      <p style={{ fontSize: 11, color: '#8a95a5' }}>
        Các checkbox cùng "Nhóm exclusive" chỉ được tick 1 (giống radio).
      </p>
      {block.items.map((it, i) => (
        <Card size="small" key={i} style={{ marginBottom: 8, background: '#fafafa' }}
          title={`Checkbox #${i + 1}`}
          extra={<Button danger size="small" type="text" icon={<Trash2 size={12} />} onClick={() => removeItem(i)} />}>
          <Form.Item label="Nhãn">
            <Input size="small" value={it.label} onChange={(e) => updateItem(i, { label: e.target.value })} />
          </Form.Item>
          <Form.Item label="Tên field">
            <Input size="small" value={it.name}
              onChange={(e) => updateItem(i, { name: e.target.value.replace(/[^a-z0-9_]/g, '') })} />
          </Form.Item>
          <Form.Item label="Nhóm exclusive (tùy chọn)">
            <Input size="small" value={it.exclusiveGroup ?? ''}
              onChange={(e) => updateItem(i, { exclusiveGroup: e.target.value || undefined })}
              placeholder="VD: kind" />
          </Form.Item>
          <Form.Item label="Mặc định tick" valuePropName="checked">
            <Switch size="small" checked={!!it.default} onChange={(v) => updateItem(i, { default: v })} />
          </Form.Item>
        </Card>
      ))}
      <Button block type="dashed" icon={<Plus size={13} />} onClick={addItem}>
        Thêm checkbox
      </Button>
    </Form>
  );
}

// ----------------- TABLE -----------------
function TableEditor({ block, onChange }: { block: TableBlock; onChange: (b: TableBlock) => void }) {
  const updateCol = (idx: number, patch: Partial<TableColumn>) => {
    const columns = [...block.columns];
    columns[idx] = { ...columns[idx], ...patch };
    onChange({ ...block, columns });
  };
  const addCol = () => {
    onChange({ ...block, columns: [...block.columns, { name: 'col_' + Date.now(), label: 'Cột mới', input: 'text' }] });
  };
  const removeCol = (idx: number) => {
    onChange({ ...block, columns: block.columns.filter((_, i) => i !== idx) });
  };
  const moveCol = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= block.columns.length) return;
    const columns = [...block.columns];
    [columns[idx], columns[newIdx]] = [columns[newIdx], columns[idx]];
    onChange({ ...block, columns });
  };

  // Totals
  const updateTotal = (idx: number, patch: Partial<TableTotal>) => {
    const totals = [...(block.totals ?? [])];
    totals[idx] = { ...totals[idx], ...patch };
    onChange({ ...block, totals });
  };
  const addTotal = () => {
    const totals = [...(block.totals ?? []), { name: 'tong_' + Date.now(), label: 'Cộng', sumColumn: '' }];
    onChange({ ...block, totals });
  };
  const removeTotal = (idx: number) => {
    const totals = (block.totals ?? []).filter((_, i) => i !== idx);
    onChange({ ...block, totals });
  };

  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Tên bảng (data key)" extra="Đây là tên dùng trong placeholder Word: {{#tên_bảng}}...{{/tên_bảng}}">
        <Input value={block.name}
          onChange={(e) => onChange({ ...block, name: e.target.value.replace(/[^a-z0-9_]/g, '') })} />
      </Form.Item>
      <Form.Item label="Cho phép thêm/xóa dòng" valuePropName="checked">
        <Switch checked={!!block.addable} onChange={(v) => onChange({ ...block, addable: v })} />
      </Form.Item>
      <Form.Item label="Số dòng tối thiểu">
        <InputNumber value={block.minRows ?? 1} min={0}
          onChange={(v) => onChange({ ...block, minRows: v ?? 1 })} />
      </Form.Item>

      <Divider />
      <h4 style={{ fontSize: 12, fontWeight: 600 }}>Cột ({block.columns.length}) — kéo thả để đổi thứ tự</h4>
      <SortableList
        items={block.columns.map((c, i) => ({ id: `col-${i}-${c.name}`, data: c }))}
        onReorder={(newItems) => onChange({ ...block, columns: newItems.map(i => i.data) })}
        renderItem={(item, dragHandle, i) => {
          const c = item.data;
          // available vars cho computed của row: tất cả cột number trong cùng table
          const colVars = block.columns
            .filter(cc => cc.input === 'number' || cc.computed)
            .map(cc => cc.name);
          return (
            <Card size="small" style={{ marginBottom: 8, background: '#fafafa' }}
              title={
                <Space size={4}>
                  {dragHandle}
                  <span style={{ fontSize: 12 }}>Cột #{i + 1}</span>
                </Space>
              }
              extra={<Button danger size="small" type="text" icon={<Trash2 size={12} />} onClick={() => removeCol(i)} />}>
              <Form.Item label="Nhãn cột">
                <Input size="small" value={c.label} onChange={(e) => updateCol(i, { label: e.target.value })} />
              </Form.Item>
              <Form.Item label="Tên trường">
                <Input size="small" value={c.name}
                  onChange={(e) => updateCol(i, { name: e.target.value.replace(/[^a-zA-Z0-9_@]/g, '') })} />
              </Form.Item>
              <Form.Item label="Loại nhập">
                <Select size="small" value={c.input} onChange={(v) => updateCol(i, { input: v })}
                  options={[
                    { value: 'text', label: 'Văn bản' },
                    { value: 'number', label: 'Số' },
                    { value: 'date', label: 'Ngày' },
                    { value: 'auto_index', label: 'Tự đánh số (1, 2, 3...)' },
                  ]} />
              </Form.Item>
              <Form.Item label="Độ rộng (px)">
                <InputNumber size="small" value={c.width ?? undefined} min={30}
                  onChange={(v) => updateCol(i, { width: v ?? undefined })} placeholder="auto" />
              </Form.Item>
              <Form.Item label="Mặc định">
                <Input size="small" value={c.default ?? ''}
                  onChange={(e) => updateCol(i, { default: e.target.value || undefined })} />
              </Form.Item>
              <Form.Item label="Công thức tính">
                <ExpressionBuilder
                  value={c.computed}
                  onChange={(v) => updateCol(i, { computed: v })}
                  availableVars={colVars}
                />
              </Form.Item>
            </Card>
          );
        }}
      />
      <Button block type="dashed" icon={<Plus size={13} />} onClick={addCol}>
        Thêm cột
      </Button>

      <Divider />
      <h4 style={{ fontSize: 12, fontWeight: 600 }}>Tổng cộng</h4>
      {(block.totals ?? []).map((t, i) => (
        <Card size="small" key={i} style={{ marginBottom: 8, background: '#fafafa' }}
          extra={<Button danger size="small" type="text" icon={<Trash2 size={12} />} onClick={() => removeTotal(i)} />}>
          <Form.Item label="Tên field tổng">
            <Input size="small" value={t.name}
              onChange={(e) => updateTotal(i, { name: e.target.value.replace(/[^a-z0-9_]/g, '') })} />
          </Form.Item>
          <Form.Item label="Nhãn">
            <Input size="small" value={t.label} onChange={(e) => updateTotal(i, { label: e.target.value })} />
          </Form.Item>
          <Form.Item label="Cột cần SUM">
            <Select size="small" value={t.sumColumn || undefined}
              onChange={(v) => updateTotal(i, { sumColumn: v })}
              options={block.columns.filter(c => c.input === 'number').map(c => ({ value: c.name, label: c.label }))} />
          </Form.Item>
        </Card>
      ))}
      <Button block type="dashed" icon={<Plus size={13} />} onClick={addTotal}>
        Thêm tổng
      </Button>
    </Form>
  );
}

// ----------------- CHECKLIST -----------------
function ChecklistEditor({ block, onChange }: { block: ChecklistBlock; onChange: (b: ChecklistBlock) => void }) {
  const updateItem = (idx: number, patch: Partial<typeof block.items[0]>) => {
    const items = [...block.items];
    items[idx] = { ...items[idx], ...patch };
    onChange({ ...block, items });
  };
  const addItem = () => {
    onChange({ ...block, items: [...block.items, { name: 'item_' + Date.now(), label: 'Mục mới' }] });
  };
  const removeItem = (idx: number) => {
    onChange({ ...block, items: block.items.filter((_, i) => i !== idx) });
  };
  const moveItem = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= block.items.length) return;
    const items = [...block.items];
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    onChange({ ...block, items });
  };

  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Nhãn nhóm">
        <Input value={block.label} onChange={(e) => onChange({ ...block, label: e.target.value })} />
      </Form.Item>
      <Form.Item label="Số cột hiển thị">
        <Select value={block.columns ?? 1}
          onChange={(v) => onChange({ ...block, columns: v as any })}
          options={[{ value: 1, label: '1 cột' }, { value: 2, label: '2 cột' }]} />
      </Form.Item>
      <Divider />
      <h4 style={{ fontSize: 12, fontWeight: 600 }}>Mục ({block.items.length}) — kéo thả để đổi thứ tự</h4>
      <SortableList
        items={block.items.map((it, i) => ({ id: `cl-${i}-${it.name}`, data: it }))}
        onReorder={(newItems) => onChange({ ...block, items: newItems.map(i => i.data) })}
        renderItem={(item, dragHandle, i) => {
          const it = item.data;
          return (
            <Card size="small" style={{ marginBottom: 8, background: '#fafafa' }}
              title={
                <Space size={4}>
                  {dragHandle}
                  <span style={{ fontSize: 12 }}>#{i + 1}</span>
                </Space>
              }
              extra={<Button danger size="small" type="text" icon={<Trash2 size={12} />} onClick={() => removeItem(i)} />}>
              <Form.Item label="Nhãn">
                <Input size="small" value={it.label} onChange={(e) => updateItem(i, { label: e.target.value })} />
              </Form.Item>
              <Form.Item label="Tên field">
                <Input size="small" value={it.name}
                  onChange={(e) => updateItem(i, { name: e.target.value.replace(/[^a-z0-9_]/g, '') })} />
              </Form.Item>
              <Form.Item label="Có ô nhập text kèm" valuePropName="checked"
                extra="Khi tick checkbox, hiện ô input để user gõ thêm thông tin">
                <Switch size="small" checked={!!it.withInput} onChange={(v) => updateItem(i, { withInput: v })} />
              </Form.Item>
              {it.withInput && (
                <Form.Item label="Placeholder ô input">
                  <Input size="small" value={it.inputPlaceholder ?? ''}
                    onChange={(e) => updateItem(i, { inputPlaceholder: e.target.value || undefined })} />
                </Form.Item>
              )}
            </Card>
          );
        }}
      />
      <Button block type="dashed" icon={<Plus size={13} />} onClick={addItem}>
        Thêm mục
      </Button>
    </Form>
  );
}

// ----------------- SIGNATURES -----------------
function SignaturesEditor({ block, onChange }: { block: SignaturesBlock; onChange: (b: SignaturesBlock) => void }) {
  const updateItem = (idx: number, patch: Partial<typeof block.items[0]>) => {
    const items = [...block.items];
    items[idx] = { ...items[idx], ...patch };
    onChange({ ...block, items });
  };
  const updateSignerCfg = (idx: number, cfgPatch: any) => {
    const item = block.items[idx];
    const cfg = { ...(item.signerConfig ?? { mode: 'NONE' }), ...cfgPatch };
    updateItem(idx, { signerConfig: cfg });
  };
  const addItem = () => {
    if (block.items.length >= 6) return;
    onChange({ ...block, items: [...block.items, { label: 'Người ký' }] });
  };
  const removeItem = (idx: number) => {
    onChange({ ...block, items: block.items.filter((_, i) => i !== idx) });
  };

  // Load users + departments cho dropdown FIXED_USER / SPECIFIC dept
  // V1.0.63i: pageSize 1000 để không bị thiếu nhân sự (trước đây 200 → thiếu nếu BV >200 user)
  const { data: users = [] } = useQuery({
    queryKey: ['users-for-signer'],
    queryFn: () => userService.list({ pageSize: 1000 }).then(r => r.items),
    staleTime: 5 * 60 * 1000,
  });
  const { data: orgTree = [] } = useQuery({
    queryKey: ['org-tree-for-signer'],
    queryFn: () => orgService.getTree(),
    staleTime: 5 * 60 * 1000,
  });
  // Flatten org tree để dùng trong Select
  const deptOptions = (() => {
    const out: { value: string; label: string }[] = [];
    const walk = (n: any, depth = 0) => {
      out.push({ value: n.id, label: '— '.repeat(depth) + n.name });
      n.children?.forEach((c: any) => walk(c, depth + 1));
    };
    orgTree.forEach((n: any) => walk(n));
    return out;
  })();

  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Layout">
        <Select size="small" value={block.layout ?? 'row'}
          onChange={(v) => onChange({ ...block, layout: v as 'row' | 'grid' })}
          options={[
            { value: 'row', label: 'Hàng ngang (truyền thống)' },
            { value: 'grid', label: 'Lưới có viền (mẫu thanh lý, đổi giá DV)' },
          ]} />
      </Form.Item>
      <p style={{ fontSize: 11, color: '#8a95a5' }}>Tối đa 6 cột chữ ký.</p>
      {block.items.map((s, i) => {
        const cfg = s.signerConfig ?? { mode: 'NONE' as const };
        return (
          <Card size="small" key={i} style={{ marginBottom: 8, background: '#fafafa' }}
            title={`Cột ${i + 1}${cfg.mode !== 'NONE' ? ' • Đã cấu hình' : ''}`}
            extra={<Button danger size="small" type="text" icon={<Trash2 size={12} />} onClick={() => removeItem(i)} />}>
            <Form.Item label="Nhãn (chức danh)">
              <Input size="small" value={s.label} onChange={(e) => updateItem(i, { label: e.target.value })} />
            </Form.Item>
            <Form.Item label="Tự fill tên user đăng nhập" valuePropName="checked"
              extra="Tick cho cột 'Người đề nghị' để tự điền tên user">
              <Switch size="small" checked={!!s.autoFillUser}
                onChange={(v) => updateItem(i, { autoFillUser: v })} />
            </Form.Item>

            {/* ─── Phase A: Pre-configured signer ─── */}
            <div style={{
              borderTop: '1px dashed #d4d9df', marginTop: 10, paddingTop: 10,
              background: '#f0f5ff', padding: '10px 12px', borderRadius: 6,
              marginLeft: -12, marginRight: -12, marginBottom: -12,
            }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: '#1d39c4' }}>
                ✍️ Người ký mặc định (Phase A)
              </div>

              <Form.Item label="Chế độ" style={{ marginBottom: 8 }}>
                <Select size="small" value={cfg.mode}
                  onChange={(v) => updateSignerCfg(i, { mode: v })}
                  options={[
                    { value: 'NONE', label: '❌ Không cấu hình (user chọn lúc tạo phiếu)' },
                    { value: 'CREATOR', label: '✍️ Người tạo yêu cầu (user đang đăng nhập)' },
                    { value: 'FIXED_USER', label: '👤 Cố định 1 người' },
                    { value: 'ROLE', label: '🎯 Theo vai trò (động)' },
                  ]} />
              </Form.Item>

              {/* FIXED_USER: chọn user */}
              {cfg.mode === 'FIXED_USER' && (
                <Form.Item label="Chọn người ký cố định" style={{ marginBottom: 8 }}>
                  <Select
                    size="small"
                    showSearch
                    optionFilterProp="label"
                    placeholder="Tìm user..."
                    value={cfg.fixedUserId}
                    onChange={(v) => {
                      const u = users.find((x: any) => x.id === v);
                      updateSignerCfg(i, {
                        fixedUserId: v,
                        fixedUserName: u?.fullName,
                        fixedUserPosition: u?.position,
                      });
                    }}
                    options={users.map((u: any) => ({
                      value: u.id,
                      label: `${u.fullName} — ${u.position ?? ''} (${u.department?.name ?? ''})`,
                    }))}
                  />
                </Form.Item>
              )}

              {/* ROLE: chọn role + scope */}
              {cfg.mode === 'ROLE' && (
                <>
                  <Form.Item label="Vai trò" style={{ marginBottom: 8 }}>
                    <Select size="small" value={cfg.role}
                      onChange={(v) => updateSignerCfg(i, { role: v })}
                      options={[
                        { value: 'DEPT_HEAD', label: 'Trưởng phòng (manager_id của khoa)' },
                        { value: 'BOD', label: 'Ban giám đốc' },
                        { value: 'SYS_ADMIN', label: 'Quản trị hệ thống' },
                      ]}
                      placeholder="Chọn vai trò..." />
                  </Form.Item>
                  <Form.Item label="Phạm vi khoa/phòng" style={{ marginBottom: 8 }}>
                    <Select size="small" value={cfg.departmentScope ?? 'SAME_AS_DOC'}
                      onChange={(v) => updateSignerCfg(i, { departmentScope: v })}
                      options={[
                        { value: 'SAME_AS_DOC', label: 'Cùng khoa với phiếu (động)' },
                        { value: 'SPECIFIC', label: 'Chỉ định khoa cụ thể' },
                      ]} />
                  </Form.Item>
                  {cfg.departmentScope === 'SPECIFIC' && (
                    <Form.Item label="Khoa/Phòng cụ thể" style={{ marginBottom: 8 }}>
                      <Select size="small" showSearch optionFilterProp="label"
                        value={cfg.specificDepartmentId}
                        onChange={(v) => updateSignerCfg(i, { specificDepartmentId: v })}
                        options={deptOptions} />
                    </Form.Item>
                  )}
                </>
              )}

              {cfg.mode !== 'NONE' && (
                <>
                  <Form.Item label="Thứ tự ký" extra="Số nhỏ ký trước (cho workflow tuần tự)" style={{ marginBottom: 8 }}>
                    <InputNumber size="small" min={1} max={20} value={cfg.order}
                      onChange={(v) => updateSignerCfg(i, { order: v })} />
                  </Form.Item>
                  <Form.Item label="Bắt buộc" valuePropName="checked" style={{ marginBottom: 8 }}>
                    <Switch size="small" checked={cfg.required !== false}
                      onChange={(v) => updateSignerCfg(i, { required: v })} />
                  </Form.Item>

                  {/* Phase C: Hiển thị trên PDF khi đã ký */}
                  <div style={{
                    borderTop: '1px dashed #c4d4ff', marginTop: 8, paddingTop: 8,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#1d39c4', marginBottom: 6 }}>
                      Hiện thị trên PDF sau khi ký:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                      <Form.Item label="Họ tên" valuePropName="checked" style={{ marginBottom: 4 }}>
                        <Switch size="small" checked={cfg.showName !== false}
                          onChange={(v) => updateSignerCfg(i, { showName: v })} />
                      </Form.Item>
                      <Form.Item label="Chức vụ" valuePropName="checked" style={{ marginBottom: 4 }}>
                        <Switch size="small" checked={cfg.showPosition !== false}
                          onChange={(v) => updateSignerCfg(i, { showPosition: v })} />
                      </Form.Item>
                      <Form.Item label="Ngày ký" valuePropName="checked" style={{ marginBottom: 0 }}>
                        <Switch size="small" checked={cfg.showDate !== false}
                          onChange={(v) => updateSignerCfg(i, { showDate: v })} />
                      </Form.Item>
                      <Form.Item label="Mã SigCode" valuePropName="checked" style={{ marginBottom: 0 }}>
                        <Switch size="small" checked={cfg.showSigCode !== false}
                          onChange={(v) => updateSignerCfg(i, { showSigCode: v })} />
                      </Form.Item>
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>
        );
      })}
      {block.items.length < 6 && (
        <Button block type="dashed" icon={<Plus size={13} />} onClick={addItem}>
          Thêm cột
        </Button>
      )}
    </Form>
  );
}

// =============================================================
// 6 EDITOR MỚI (Phase 5)
// =============================================================

function Header2ColEditor({ block, onChange }: { block: Header2ColBlock; onChange: (b: Header2ColBlock) => void }) {
  return (
    <Form layout="vertical" size="small">
      <p style={{ fontSize: 11, color: '#8a95a5' }}>VD: "Sở Y tế Hà Nội" / "CHXHCN Việt Nam"</p>
      <Card size="small" title="Cột TRÁI" style={{ marginBottom: 8 }}>
        <Form.Item label="Dòng 1">
          <Input size="small" value={block.left.line1}
            onChange={(e) => onChange({ ...block, left: { ...block.left, line1: e.target.value } })} />
        </Form.Item>
        <Form.Item label="Dòng 2 (tùy chọn)">
          <Input size="small" value={block.left.line2 ?? ''}
            onChange={(e) => onChange({ ...block, left: { ...block.left, line2: e.target.value || undefined } })} />
        </Form.Item>
      </Card>
      <Card size="small" title="Cột PHẢI">
        <Form.Item label="Dòng 1">
          <Input size="small" value={block.right.line1}
            onChange={(e) => onChange({ ...block, right: { ...block.right, line1: e.target.value } })} />
        </Form.Item>
        <Form.Item label="Dòng 2 (tùy chọn)">
          <Input size="small" value={block.right.line2 ?? ''}
            onChange={(e) => onChange({ ...block, right: { ...block.right, line2: e.target.value || undefined } })} />
        </Form.Item>
      </Card>
    </Form>
  );
}

function PartySectionEditor({ block, onChange }: { block: PartySectionBlock; onChange: (b: PartySectionBlock) => void }) {
  return (
    <Form layout="vertical" size="small">
      <p style={{ fontSize: 11, color: '#8a95a5' }}>Dùng cho "Bên A/Bên B" trong biên bản giao nhận. User có thể thêm/xóa đại diện khi fill form.</p>
      <Form.Item label="Tiêu đề section">
        <Input size="small" value={block.title}
          onChange={(e) => onChange({ ...block, title: e.target.value })} />
      </Form.Item>
      <Form.Item label="Tên field (trong Word: {{name}})">
        <Input size="small" value={block.name}
          onChange={(e) => onChange({ ...block, name: e.target.value })} />
      </Form.Item>
      <Form.Item label="Hiện 'Địa chỉ' (cho mẫu giao nhận NCC)" valuePropName="checked">
        <Switch size="small" checked={!!block.showAddress}
          onChange={(v) => onChange({ ...block, showAddress: v })} />
      </Form.Item>
      <Form.Item label="Số đại diện mặc định">
        <Select size="small" value={block.defaultReps ?? 2}
          onChange={(v) => onChange({ ...block, defaultReps: v })}
          options={[1, 2, 3, 4].map(n => ({ value: n, label: `${n} đại diện` }))} />
      </Form.Item>
      <Form.Item label="Tối đa đại diện">
        <Select size="small" value={block.maxReps ?? 6}
          onChange={(v) => onChange({ ...block, maxReps: v })}
          options={[2, 3, 4, 5, 6, 8, 10].map(n => ({ value: n, label: `${n}` }))} />
      </Form.Item>
    </Form>
  );
}

function BankInfoEditor({ block, onChange }: { block: BankInfoBlock; onChange: (b: BankInfoBlock) => void }) {
  return (
    <Form layout="vertical" size="small">
      <p style={{ fontSize: 11, color: '#8a95a5' }}>3 trường: Người thụ hưởng / Số TK / Ngân hàng. Có thể chỉ hiện khi field khác = giá trị nào đó (vd: chỉ hiện khi 'Hình thức = Chuyển khoản').</p>
      <Form.Item label="Tên field 'Người thụ hưởng'">
        <Input size="small" value={block.beneficiaryName ?? 'beneficiary'}
          onChange={(e) => onChange({ ...block, beneficiaryName: e.target.value })} />
      </Form.Item>
      <Form.Item label="Tên field 'Số tài khoản'">
        <Input size="small" value={block.accountNumberName ?? 'account_number'}
          onChange={(e) => onChange({ ...block, accountNumberName: e.target.value })} />
      </Form.Item>
      <Form.Item label="Tên field 'Ngân hàng'">
        <Input size="small" value={block.bankName ?? 'bank_name'}
          onChange={(e) => onChange({ ...block, bankName: e.target.value })} />
      </Form.Item>
      <Form.Item label="Chỉ hiện khi field khác có giá trị (tùy chọn)"
        extra='VD: tên field "payment_method", giá trị "transfer" → chỉ hiện khi user chọn Chuyển khoản'>
        <Input size="small" placeholder="Tên field điều kiện (hoặc trống = luôn hiện)"
          value={block.showWhenField ?? ''}
          onChange={(e) => onChange({ ...block, showWhenField: e.target.value || undefined })} />
      </Form.Item>
      {block.showWhenField && (
        <Form.Item label="Giá trị cần match">
          <Input size="small" value={block.showWhenValue ?? ''}
            onChange={(e) => onChange({ ...block, showWhenValue: e.target.value })} />
        </Form.Item>
      )}
    </Form>
  );
}

function InfoGridEditor({ block, onChange }: { block: InfoGridBlock; onChange: (b: InfoGridBlock) => void }) {
  const updateCell = (idx: number, patch: Partial<typeof block.cells[0]>) => {
    const cells = [...block.cells];
    cells[idx] = { ...cells[idx], ...patch };
    onChange({ ...block, cells });
  };
  const addCell = () => {
    if (block.cells.length >= 4) return;
    onChange({ ...block, cells: [...block.cells, { label: 'Mục mới', name: 'cell_' + Date.now(), input: 'text' }] });
  };
  return (
    <Form layout="vertical" size="small">
      <p style={{ fontSize: 11, color: '#8a95a5' }}>Grid 2-4 ô có viền. VD: "Bộ phận yêu cầu / Bộ phận tiếp nhận"</p>
      <Form.Item label="Số cột">
        <Select size="small" value={block.columns}
          onChange={(v) => onChange({ ...block, columns: v })}
          options={[2, 3, 4].map(n => ({ value: n, label: `${n} cột` }))} />
      </Form.Item>
      {block.cells.map((c, i) => (
        <Card size="small" key={i} style={{ marginBottom: 8, background: '#fafafa' }}
          title={`Ô ${i + 1}`}
          extra={<Button danger size="small" type="text" icon={<Trash2 size={12} />}
            onClick={() => onChange({ ...block, cells: block.cells.filter((_, idx) => idx !== i) })} />}>
          <Form.Item label="Nhãn">
            <Input size="small" value={c.label} onChange={(e) => updateCell(i, { label: e.target.value })} />
          </Form.Item>
          <Form.Item label="Tên field">
            <Input size="small" value={c.name} onChange={(e) => updateCell(i, { name: e.target.value })} />
          </Form.Item>
          <Form.Item label="Loại nhập">
            <Select size="small" value={c.input ?? 'text'}
              onChange={(v) => updateCell(i, { input: v as 'text' | 'date' })}
              options={[
                { value: 'text', label: 'Text' },
                { value: 'date', label: 'Ngày tháng' },
              ]} />
          </Form.Item>
          <Form.Item label="Tự fill (tùy chọn)">
            <Select size="small" allowClear value={c.autoFill}
              onChange={(v) => updateCell(i, { autoFill: v })}
              options={[
                { value: 'current_user_name', label: 'Tên user' },
                { value: 'current_user_dept', label: 'Phòng ban user' },
                { value: 'today', label: 'Ngày hôm nay' },
              ]} />
          </Form.Item>
        </Card>
      ))}
      {block.cells.length < 4 && (
        <Button block type="dashed" icon={<Plus size={13} />} onClick={addCell}>
          Thêm ô
        </Button>
      )}
    </Form>
  );
}

function SpacerEditor({ block, onChange }: { block: SpacerBlock; onChange: (b: SpacerBlock) => void }) {
  return (
    <Form layout="vertical" size="small">
      <Form.Item label="Loại">
        <Select size="small" value={block.kind ?? 'space'}
          onChange={(v) => onChange({ ...block, kind: v as 'line' | 'space' })}
          options={[
            { value: 'space', label: 'Khoảng trống' },
            { value: 'line', label: 'Đường kẻ ngang' },
          ]} />
      </Form.Item>
      <Form.Item label="Chiều cao (px)">
        <InputNumber size="small" min={4} max={100} value={block.height ?? 16}
          onChange={(v) => onChange({ ...block, height: v ?? 16 })} style={{ width: '100%' }} />
      </Form.Item>
    </Form>
  );
}

function TableGroupedEditor({ block, onChange }: { block: TableGroupedBlock; onChange: (b: TableGroupedBlock) => void }) {
  // Editor đơn giản: cho sửa tên bảng + JSON groups + JSON columns
  // (UI đẹp hơn sẽ làm sau khi có nhu cầu thực tế)
  return (
    <Form layout="vertical" size="small">
      <p style={{ fontSize: 11, color: '#8a95a5' }}>Bảng có header 2 cấp (nhóm cột + cột con). Chỉnh JSON trực tiếp ở đây — UI giàu sẽ thêm sau.</p>
      <Form.Item label="Tên bảng (trong Word: {{#name}}...{{/name}})">
        <Input size="small" value={block.name}
          onChange={(e) => onChange({ ...block, name: e.target.value })} />
      </Form.Item>
      <Form.Item label="Cho phép thêm/xóa dòng" valuePropName="checked">
        <Switch size="small" checked={block.addable !== false}
          onChange={(v) => onChange({ ...block, addable: v })} />
      </Form.Item>
      <Form.Item label="Số dòng tối thiểu">
        <InputNumber size="small" min={1} max={20} value={block.minRows ?? 1}
          onChange={(v) => onChange({ ...block, minRows: v ?? 1 })} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item label="Nhóm cột (JSON)" extra='[{"label": "TỪ", "colspan": 8}, {"label": "THÀNH", "colspan": 7}]'>
        <Input.TextArea rows={3} value={JSON.stringify(block.groups, null, 2)}
          onChange={(e) => {
            try {
              const groups = JSON.parse(e.target.value);
              onChange({ ...block, groups });
            } catch { /* ignore parse errors khi đang gõ */ }
          }} style={{ fontFamily: 'monospace', fontSize: 11 }} />
      </Form.Item>
      <Form.Item label="Cột con (JSON)"
        extra='Tổng số cột phải = tổng colspan của các nhóm. Mỗi cột: {"name", "label", "input": "text|number|auto_index", "width"}'>
        <Input.TextArea rows={6} value={JSON.stringify(block.columns, null, 2)}
          onChange={(e) => {
            try {
              const columns = JSON.parse(e.target.value);
              onChange({ ...block, columns });
            } catch { /* ignore parse errors khi đang gõ */ }
          }} style={{ fontFamily: 'monospace', fontSize: 11 }} />
      </Form.Item>
    </Form>
  );
}

// =============================================================
// Defaults cho từng loại block khi mới thêm
// =============================================================
function createDefaultBlock(type: FormBlock['type']): FormBlock {
  switch (type) {
    case 'header': return { type: 'header', showLogo: true, showCompany: true };
    case 'title': return { type: 'title', text: 'TIÊU ĐỀ' };
    case 'section_title': return { type: 'section_title', text: 'I - Mục mới' };
    case 'text': return { type: 'text', text: 'Đoạn văn bản', align: 'left' };
    case 'field': return {
      type: 'field', name: 'field_' + Date.now(),
      label: 'Trường mới', input: 'text', required: false,
    };
    case 'field_row': return {
      type: 'field_row',
      fields: [
        { name: 'col1', label: 'Cột 1', input: 'text' },
        { name: 'col2', label: 'Cột 2', input: 'text' },
      ],
    };
    case 'checkbox_row': return {
      type: 'checkbox_row',
      items: [
        { name: 'option_1', label: 'Tùy chọn 1' },
        { name: 'option_2', label: 'Tùy chọn 2' },
      ],
    };
    case 'table': return {
      type: 'table',
      name: 'items',
      addable: true,
      minRows: 1,
      columns: [
        { name: '@index', label: 'TT', input: 'auto_index', width: 40 },
        { name: 'noi_dung', label: 'Nội dung', input: 'text' },
        { name: 'so_luong', label: 'Số lượng', input: 'number', width: 80 },
        { name: 'don_gia', label: 'Đơn giá', input: 'number', width: 110 },
        { name: 'thanh_tien', label: 'Thành tiền', input: 'number', width: 130, computed: 'so_luong * don_gia' },
      ],
      totals: [{ name: 'tong_cong', label: 'Cộng', sumColumn: 'thanh_tien' }],
    };
    case 'checklist': return {
      type: 'checklist',
      label: 'Danh sách',
      columns: 2,
      items: [
        { name: 'item_1', label: 'Mục 1', withInput: true },
        { name: 'item_2', label: 'Mục 2' },
      ],
    };
    case 'signatures': return {
      type: 'signatures',
      items: [
        { label: 'Người duyệt' },
        { label: 'Người đề nghị', autoFillUser: true },
      ],
    };
    case 'header_2col': return {
      type: 'header_2col',
      left: { line1: 'CÔNG TY CP KINH DOANH HƯNG VIỆT' },
      right: { line1: 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM', line2: 'Độc lập – Tự do – Hạnh phúc' },
    };
    case 'table_grouped': return {
      type: 'table_grouped',
      name: 'items',
      addable: true,
      minRows: 1,
      groups: [
        { label: 'NHÓM 1', colspan: 3 },
        { label: 'NHÓM 2', colspan: 3 },
      ],
      columns: [
        { name: '@index', label: 'STT', input: 'auto_index', width: 40 },
        { name: 'col_a', label: 'Cột A', input: 'text' },
        { name: 'col_b', label: 'Cột B', input: 'text' },
        { name: 'col_c', label: 'Cột C', input: 'text' },
        { name: 'col_d', label: 'Cột D', input: 'text' },
        { name: 'col_e', label: 'Cột E', input: 'text' },
      ],
    };
    case 'party_section': return {
      type: 'party_section',
      title: 'Bên A (Bên giao hàng)',
      name: 'party_a',
      defaultReps: 2,
      maxReps: 5,
      showAddress: false,
    };
    case 'bank_info': return {
      type: 'bank_info',
      beneficiaryName: 'beneficiary',
      accountNumberName: 'account_number',
      bankName: 'bank_name',
      showWhenField: 'payment_method',
      showWhenValue: 'transfer',
    };
    case 'info_grid': return {
      type: 'info_grid',
      columns: 2,
      cells: [
        { label: 'Bộ phận yêu cầu', name: 'dept_request', input: 'text' },
        { label: 'Bộ phận tiếp nhận', name: 'dept_receive', input: 'text' },
      ],
    };
    case 'spacer': return {
      type: 'spacer',
      kind: 'space',
      height: 16,
    };
  }
}

// =============================================================
// Live Test Mode — admin fill data thử để kiểm tra
// =============================================================
function LiveTestMode({
  schema, settings, logoUrl, user, values, tables, onSetValues, onSetTables,
}: {
  schema: FormSchema;
  settings: any;
  logoUrl: string | null;
  user: any;
  values: Record<string, string>;
  tables: Record<string, any[]>;
  onSetValues: (v: Record<string, string>) => void;
  onSetTables: (t: Record<string, any[]>) => void;
}) {
  // Compute table totals
  const tableTotals: Record<string, number> = useMemo(() => {
    const t: Record<string, number> = {};
    for (const b of schema.blocks) {
      if (b.type !== 'table' || !b.totals) continue;
      const rows = tables[b.name] ?? [];
      for (const total of b.totals) {
        const sum = rows.reduce((s, r) => s + (parseFloat(r[total.sumColumn]) || 0), 0);
        t[total.name] = sum;
      }
    }
    return t;
  }, [schema, tables]);

  // Computed values
  const finalValues = useMemo(() => {
    const ctx: Record<string, number> = {};
    Object.entries(values).forEach(([k, v]) => {
      const n = parseFloat(v);
      if (!isNaN(n)) ctx[k] = n;
    });
    Object.entries(tableTotals).forEach(([k, v]) => { ctx[k] = v; });

    const cv: Record<string, string> = { ...values };
    for (const b of schema.blocks) {
      const compute = (f: any) => {
        if (f.computed) {
          // Reuse evalComputed logic via simple case (avoid circular import)
          try {
            const expr = String(f.computed).replace(/[^a-zA-Z0-9_+\-*/().,\s]/g, '');
            const args = Array.from(new Set(expr.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || []))
              .filter(v => !['max', 'min', 'abs', 'round', 'floor', 'ceil'].includes(v));
            const argVals = args.map(v => ctx[v] ?? 0);
            // eslint-disable-next-line no-new-func
            const fn = new Function('max', 'min', 'abs', 'round', 'floor', 'ceil', ...args, `return (${expr});`);
            const r = fn(Math.max, Math.min, Math.abs, Math.round, Math.floor, Math.ceil, ...argVals);
            const num = typeof r === 'number' && isFinite(r) ? r : 0;
            cv[f.name] = String(Math.round(num));
            ctx[f.name] = num;
          } catch { cv[f.name] = '0'; }
        } else if (f.computedWords) {
          const num = ctx[f.computedWords] ?? 0;
          cv[f.name] = num > 0 ? `(số ${Math.round(num).toLocaleString('vi-VN')} bằng chữ)` : '';
        }
      };
      if (b.type === 'field') compute(b);
      if (b.type === 'field_row') b.fields.forEach(compute);
    }
    return cv;
  }, [schema, values, tableTotals]);

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 16, background: '#e8eaed' }}>
      <Alert
        type="info"
        showIcon
        message="Chế độ Live Test"
        description="Fill thử data để xem form hoạt động đúng không. Data nhập đây CHỈ phục vụ test, không lưu vào DB."
        style={{ maxWidth: 794, margin: '0 auto 12px', }}
      />

      <div style={{
        maxWidth: 794, margin: '0 auto', background: '#fff',
        padding: '32px 48px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        fontFamily: '"Times New Roman", Times, serif',
        color: '#000', fontSize: 14, lineHeight: 1.6,
      }}>
        {schema.blocks.map((block, idx) => (
          <BlockRenderer
            key={idx}
            block={block}
            values={finalValues}
            tables={tables}
            tableTotals={tableTotals}
            settings={settings}
            logoUrl={logoUrl}
            user={user}
            formCode={schema.formCode}
            onSetValue={(name, v) => onSetValues({ ...values, [name]: v })}
            onToggleExclusive={(group, name) => {
              const next = { ...values, [name]: values[name] === '1' ? '0' : '1' };
              if (next[name] === '1') {
                for (const b of schema.blocks) {
                  if (b.type === 'checkbox_row') {
                    for (const it of b.items) {
                      if (it.exclusiveGroup === group && it.name !== name) next[it.name] = '0';
                    }
                  }
                }
              }
              onSetValues(next);
            }}
            onUpdateTableCell={(tableName, rowIdx, col, value, columns) => {
              const rows = [...(tables[tableName] ?? [])];
              rows[rowIdx] = { ...rows[rowIdx], [col]: value };
              for (const c of columns) {
                if (c.computed) {
                  try {
                    const ctx: Record<string, number> = {};
                    for (const k of Object.keys(rows[rowIdx])) {
                      const n = parseFloat(rows[rowIdx][k]);
                      if (!isNaN(n)) ctx[k] = n;
                    }
                    const expr = String(c.computed).replace(/[^a-zA-Z0-9_+\-*/().,\s]/g, '');
                    const args = Array.from(new Set(expr.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || []))
                      .filter(v => !['max', 'min', 'abs', 'round', 'floor', 'ceil'].includes(v));
                    const argVals = args.map(v => ctx[v] ?? 0);
                    // eslint-disable-next-line no-new-func
                    const fn = new Function('max', 'min', 'abs', 'round', 'floor', 'ceil', ...args, `return (${expr});`);
                    const r = fn(Math.max, Math.min, Math.abs, Math.round, Math.floor, Math.ceil, ...argVals);
                    rows[rowIdx][c.name] = String(Math.round(r));
                  } catch { /* skip */ }
                }
              }
              onSetTables({ ...tables, [tableName]: rows });
            }}
            onAddTableRow={(tableName, columns) => {
              const rows = [...(tables[tableName] ?? [])];
              const newRow: any = {};
              columns.forEach((c: any) => { newRow[c.name] = c.default ?? ''; });
              rows.push(newRow);
              onSetTables({ ...tables, [tableName]: rows });
            }}
            onRemoveTableRow={(tableName, idx) => {
              const rows = [...(tables[tableName] ?? [])];
              rows.splice(idx, 1);
              onSetTables({ ...tables, [tableName]: rows });
            }}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================
// Gallery Modal — chọn schema có sẵn để áp dụng
// =============================================================
function GalleryModal({ onClose, onApply }: {
  onClose: () => void;
  onApply: (item: typeof TEMPLATE_GALLERY[0]) => void;
}) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | undefined>(undefined);

  // Lấy danh sách category unique
  const categories = useMemo(() => {
    const s = new Set(TEMPLATE_GALLERY.map(g => g.category));
    return Array.from(s);
  }, []);

  const filtered = useMemo(() => {
    return TEMPLATE_GALLERY.filter(g => {
      if (category && g.category !== category) return false;
      if (search) {
        const q = search.toLowerCase();
        return g.name.toLowerCase().includes(q) ||
               g.description.toLowerCase().includes(q) ||
               (g.formCode ?? '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [search, category]);

  return (
    <Modal
      open
      onCancel={onClose}
      title={
        <span>
          <LayoutTemplate size={16} style={{ verticalAlign: -3, marginRight: 6 }} />
          Thư viện schema mẫu sẵn ({TEMPLATE_GALLERY.length} mẫu)
        </span>
      }
      width={820}
      footer={null}
    >
      <p style={{ color: '#7d8a98', fontSize: 13, marginBottom: 12 }}>
        Chọn 1 mẫu để áp dụng — toàn bộ schema hiện tại sẽ bị thay thế.
        Sau khi áp dụng, bạn có thể tiếp tục chỉnh sửa qua Visual Builder.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Input
          placeholder="Tìm theo tên / mô tả / mã form..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ flex: 1 }}
          size="small"
        />
        <Select
          placeholder="Tất cả category"
          value={category}
          onChange={setCategory}
          allowClear
          size="small"
          style={{ width: 180 }}
          options={categories.map(c => ({ value: c, label: c }))}
        />
      </div>

      <div style={{
        display: 'grid', gap: 12,
        maxHeight: 'calc(70vh - 180px)', overflowY: 'auto',
        paddingRight: 4,
      }}>
        {filtered.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
            Không có mẫu khớp tìm kiếm
          </div>
        )}
        {filtered.map(g => (
          <Card
            key={g.id}
            hoverable
            size="small"
            onClick={() => onApply(g)}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 6,
                background: '#e6f4ff', color: '#1976D2',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <LayoutTemplate size={22} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{g.name}</div>
                <div style={{ fontSize: 12, color: '#5b6875', marginTop: 4 }}>
                  {g.description}
                </div>
                <Space size={4} style={{ marginTop: 6 }} wrap>
                  <Tag color="blue" style={{ margin: 0 }}>{g.category}</Tag>
                  {g.formCode && <Tag style={{ margin: 0 }}>{g.formCode}</Tag>}
                  <Tag color="green" style={{ margin: 0 }}>{g.schema.blocks.length} block</Tag>
                </Space>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Modal>
  );
}
